/**
 * ContextMenu — Right-click context menu on the map canvas.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Pentagon, Route, Copy, Search } from 'lucide-react';
import { useMapStore } from '../MapContext';
import { useAgentMap, useLookupParcelByPoint } from '../api';
import { toast } from 'sonner';

export default function ContextMenu() {
  const {
    contextMenu,
    setContextMenu,
    setDrawMode,
    selectedLayer,
    setActiveParcel,
    setParcelInspectorOpen,
  } = useMapStore();
  const agentMap = useAgentMap();
  const lookupParcel = useLookupParcelByPoint();

  if (!contextMenu) return null;

  const handleDetectHere = () => {
    const [lng, lat] = contextMenu.lngLat;
    setContextMenu(null);

    const triggerDetection = (parcelId?: string, entityId?: string | null) => {
      agentMap.mutate(
        parcelId
          ? {
              mode: 'detect_infrastructure',
              parcel_id: parcelId,
              entity_id: entityId || undefined,
              replace_existing: true,
            }
          : {
              mode: 'detect_infrastructure',
              geometry: { type: 'Point', coordinates: [lng, lat] },
              scan_radius_m: 120,
            },
        {
          onSuccess: () => {
            toast.success(parcelId ? 'Parcel scan started' : 'Detection started', {
              description: parcelId
                ? 'Scanning within the parcel boundary. Review items will appear shortly.'
                : 'Scanning around the clicked point for likely infrastructure.',
            });
          },
          onError: (err) => {
            toast.error('Detection failed', { description: String(err) });
          },
        }
      );
    };

    lookupParcel.mutate(
      { lng, lat },
      {
        onSuccess: (parcel) => {
          if (parcel) {
            setActiveParcel(parcel);
            setParcelInspectorOpen(true);
            triggerDetection(parcel.id, parcel.linked_entity_id);
            return;
          }
          triggerDetection();
        },
        onError: () => {
          triggerDetection();
        },
      }
    );
  };

  const items = [
    {
      icon: MapPin,
      label: 'Add marker here',
      action: () => {
        setDrawMode('point', selectedLayer?.id);
        setContextMenu(null);
      },
    },
    {
      icon: Pentagon,
      label: 'Draw polygon',
      action: () => {
        setDrawMode('polygon', selectedLayer?.id);
        setContextMenu(null);
      },
    },
    {
      icon: Route,
      label: 'Draw line',
      action: () => {
        setDrawMode('line', selectedLayer?.id);
        setContextMenu(null);
      },
    },
    { type: 'separator' as const },
    {
      icon: Search,
      label: 'Detect around here',
      sublabel: 'Parcel if matched, otherwise 120m radius',
      action: handleDetectHere,
    },
    { type: 'separator' as const },
    {
      icon: Copy,
      label: `Copy coordinates`,
      sublabel: `${contextMenu.lngLat[1].toFixed(6)}, ${contextMenu.lngLat[0].toFixed(6)}`,
      action: () => {
        navigator.clipboard.writeText(`${contextMenu.lngLat[1].toFixed(6)}, ${contextMenu.lngLat[0].toFixed(6)}`);
        toast.success('Coordinates copied');
        setContextMenu(null);
      },
    },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute z-50 bg-white rounded-xl shadow-2xl border border-gray-200 py-1.5 min-w-[200px] overflow-hidden"
        style={{ left: contextMenu.x, top: contextMenu.y }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((item, i) => {
          if ('type' in item && item.type === 'separator') {
            return <div key={i} className="border-t border-gray-100 my-1" />;
          }
          const Icon = item.icon!;
          return (
            <motion.button
              key={i}
              whileTap={{ scale: 0.97 }}
              onClick={item.action}
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
            >
              <Icon className="w-4 h-4 text-gray-400" />
              <div>
                <div className="text-xs font-medium text-gray-700">{item.label}</div>
                {'sublabel' in item && item.sublabel && (
                  <div className="text-[10px] text-gray-400 font-mono">{item.sublabel}</div>
                )}
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </AnimatePresence>
  );
}
